import { InlineNotice } from '@/components/feedback/inline-notice'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { Button } from '@/components/primitives/button'
import { KeyIcon, LockIcon, MailIcon } from '@/features/auth/components/icons'
import { LanguageSegmentedControl } from '@/features/auth/components/language-segmented-control'
import { usePortalLoginPage } from '@/features/auth/login/use-portal-login-page'
import { MobileAuthBrandHeader } from '@/features/auth/mobile/mobile-auth-brand-header'
import { MobileAuthLayout } from '@/features/auth/mobile/mobile-auth-layout'
import { setAppLanguage } from '@/i18n/i18n'

export function UserMobileLoginPage({
  loginMode = 'default',
}: {
  loginMode?: 'default' | 'password'
}) {
  const {
    clearFieldFeedback,
    errors,
    language,
    loginMutation,
    loginEntryPending,
    loginNotice,
    onSubmit,
    register,
    showEmailPasswordForm,
    showLoginDivider,
    showSsoLogin,
    ssoMutation,
    t,
    validateEmailOnBlur,
    validatePasswordOnBlur,
  } = usePortalLoginPage('user', true, loginMode)

  return (
    <MobileAuthLayout
      actions={
        <LanguageSegmentedControl
          language={language}
          onLanguageChange={(nextLanguage) => void setAppLanguage(nextLanguage)}
          t={t}
        />
      }
    >
      <div className="grid">
        <MobileAuthBrandHeader title={t('user.title')} />

        {loginNotice ? (
          <InlineNotice
            className="mt-6 text-[13px] leading-[19px]"
            role="status"
            tone={loginNotice === 'resetSuccess' ? 'success' : 'info'}
          >
            {t(
              loginNotice === 'resetSuccess'
                ? 'passwordReset.successLoginPrompt'
                : 'user.wecomResume',
            )}
          </InlineNotice>
        ) : null}

        {!loginEntryPending && errors.root?.message ? (
          <InlineNotice className="mt-6 text-[13px] leading-[19px]" role="alert" tone="danger">
            {errors.root.message}
          </InlineNotice>
        ) : null}

        {loginEntryPending ? (
          <div className="mt-8 grid justify-items-center gap-4 py-10 text-center">
            <div className="size-10 animate-spin rounded-full border-2 border-[rgba(25,91,255,0.16)] border-t-[var(--color-primary)]" />
            <p className="text-sm leading-6 text-[var(--color-text-muted)]">
              {t('user.redirectingToSso')}
            </p>
          </div>
        ) : showEmailPasswordForm ? (
          <form className="mt-8 grid gap-[18px]" onSubmit={onSubmit}>
            <div className="grid gap-2">
              <label className="text-[13px] leading-none text-[#374151]" htmlFor="user-email">
                {t('common.emailLabel')}
              </label>
              <Input
                className="h-[46px] rounded-lg border-[var(--color-control-border)] text-[15px]"
                id="user-email"
                invalid={Boolean(errors.email)}
                placeholder={t('common.emailPlaceholder')}
                prefix={<MailIcon />}
                type="email"
                {...register('email', {
                  onBlur: validateEmailOnBlur,
                  onChange: () => clearFieldFeedback('email'),
                })}
              />
              {errors.email?.message ? (
                <p className="flex items-center gap-1 text-xs leading-[17px] text-[var(--color-danger)] before:grid before:size-[13px] before:shrink-0 before:place-items-center before:rounded-full before:bg-[var(--color-danger)] before:text-[9px] before:font-bold before:text-white before:content-['!']">
                  {errors.email.message}
                </p>
              ) : null}
            </div>

            <div className="grid gap-2">
              <label className="text-[13px] leading-none text-[#374151]" htmlFor="user-password">
                {t('common.passwordLabel')}
              </label>
              <PasswordInput
                className="h-[46px] rounded-lg border-[var(--color-control-border)] text-[15px]"
                hideLabel={t('common.hidePassword')}
                id="user-password"
                invalid={Boolean(errors.password)}
                placeholder={t('common.passwordPlaceholder')}
                prefix={<LockIcon />}
                showLabel={t('common.showPassword')}
                {...register('password', {
                  onBlur: validatePasswordOnBlur,
                  onChange: () => clearFieldFeedback('password'),
                })}
              />
              {errors.password?.message ? (
                <p className="flex items-center gap-1 text-xs leading-[17px] text-[var(--color-danger)] before:grid before:size-[13px] before:shrink-0 before:place-items-center before:rounded-full before:bg-[var(--color-danger)] before:text-[9px] before:font-bold before:text-white before:content-['!']">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            <Button
              className="h-[46px] rounded-lg text-base font-bold"
              disabled={loginMutation.isPending}
              isLoading={loginMutation.isPending}
              type="submit"
            >
              {t('user.submit')}
            </Button>
          </form>
        ) : null}

        {!loginEntryPending && showSsoLogin ? (
          <div className={`grid ${showLoginDivider ? 'mt-[22px] gap-[18px]' : 'mt-[18px]'}`}>
            {showLoginDivider ? (
              <div className="flex items-center gap-4 text-xs text-[rgba(26,28,25,0.32)]">
                <div className="h-px flex-1 rounded-full bg-[var(--color-divider)]" />
                <span>{t('user.divider')}</span>
                <div className="h-px flex-1 rounded-full bg-[var(--color-divider)]" />
              </div>
            ) : null}
            <Button
              className="h-[46px] rounded-lg !border-[rgba(25,91,255,0.3)] !bg-[rgba(25,91,255,0.05)] text-[15px] font-normal !text-[var(--color-primary)] hover:!bg-[rgba(25,91,255,0.09)]"
              isLoading={ssoMutation.isPending}
              onClick={() => ssoMutation.mutate()}
              type="button"
              variant="secondary"
            >
              <KeyIcon />
              {t('user.sso')}
            </Button>
          </div>
        ) : null}
      </div>
    </MobileAuthLayout>
  )
}
